import type { ScreenerReqSegments } from '@revelio/data-access';

import {
  type AllSelectionListsWithSkillTaxonomyKey,
  FilterId,
} from '../../types/filters';

/**
 * Map segment field name (key of ScreenerReqSegments) to FilterId.
 * Reverse of mapFilterIdToSegmentField in get-screener-filters.ts.
 */
export const segmentFieldToFilterId: Partial<
  Record<keyof ScreenerReqSegments, FilterId>
> = {
  attrition: FilterId.ATTRITION_RATE,
  growth_yoy: FilterId.GROWTH_RATE,
  hiring: FilterId.HIRING_RATE,
  headcount: FilterId.HEADCOUNT,
  salary: FilterId.SALARY,
  tenure: FilterId.TENURE,
  inflow: FilterId.INFLOW,
  outflow: FilterId.OUTFLOW,
};

export const getFilterIdForSegmentField = (
  segmentField: keyof ScreenerReqSegments
): FilterId | null => segmentFieldToFilterId[segmentField] ?? null;

/**
 * Map segment people filter keys (inside segment.filter) to FilterId and type.
 * Used when converting resolved segment filters to CustomDash segmentations.
 */
export const segmentFilterKeyToFilterId: Partial<
  Record<string, { filterId: FilterId; type: 'tree' | 'list' }>
> = {
  geography: { filterId: FilterId.GEOGRAPHY, type: 'tree' },
  industry: { filterId: FilterId.INDUSTRY, type: 'tree' },
  role: { filterId: FilterId.ROLE, type: 'tree' },
  seniority: { filterId: FilterId.SENIORITY, type: 'list' },
  highest_degree: { filterId: FilterId.EDUCATION, type: 'list' },
  gender: { filterId: FilterId.GENDER, type: 'list' },
  ethnicity: { filterId: FilterId.ETHNICITY, type: 'list' },
};

/**
 * Segment metric FilterIds that use percentage (rate) values in the API.
 * API sends decimal (e.g. 0.05); we display and store as percentage (5).
 */
export const RATE_SEGMENT_FILTER_IDS = new Set<FilterId>([
  FilterId.ATTRITION_RATE,
  FilterId.GROWTH_RATE,
  FilterId.HIRING_RATE,
]);

/** Mapping for tree-type ScreenerReqFilters keys -> FilterId and levelId */
export type ScreenerTreeFilterMapping = {
  filterId: FilterId;
  levelId: AllSelectionListsWithSkillTaxonomyKey;
};

/** Mapping for list-type ScreenerReqFilters keys -> FilterId and selectionListId */
export type ScreenerListFilterMapping = {
  filterId: FilterId;
  selectionListId: string;
};

export type ScreenerFilterKeyMapping =
  | ScreenerTreeFilterMapping
  | (ScreenerListFilterMapping & { levelId?: never });

/**
 * Maps ScreenerReqFilters keys to FilterId and levelId (tree) or selectionListId (list).
 * Used when converting primary_selector or segment demographics to CustomDash filters.
 */
export const screenerFilterKeyToFilterMapping: Partial<
  Record<string, ScreenerFilterKeyMapping>
> = {
  // Company / industry (tree)
  rics_k10: { filterId: FilterId.INDUSTRY, levelId: 'rics_k10' },
  rics_k50: { filterId: FilterId.INDUSTRY, levelId: 'rics_k50' },
  rics_k400: { filterId: FilterId.INDUSTRY, levelId: 'rics_k400' },
  industry: {
    filterId: FilterId.INDUSTRY,
    levelId: 'industry' as AllSelectionListsWithSkillTaxonomyKey,
  },
  // Geography (tree) - API uses metro_area, our tree uses msa
  country: { filterId: FilterId.GEOGRAPHY, levelId: 'country' },
  region: { filterId: FilterId.GEOGRAPHY, levelId: 'region' },
  metro_area: { filterId: FilterId.GEOGRAPHY, levelId: 'msa' },
  state: {
    filterId: FilterId.GEOGRAPHY,
    levelId: 'state' as AllSelectionListsWithSkillTaxonomyKey,
  },
  // Role (tree) - API uses job_category for role_k7
  job_category: { filterId: FilterId.ROLE, levelId: 'role_k7' },
  role_k150: { filterId: FilterId.ROLE, levelId: 'role_k150' },
  role_k1500: { filterId: FilterId.ROLE, levelId: 'role_k1500' },
  role_k15000: { filterId: FilterId.ROLE, levelId: 'role_k15000' },
  // List filters (demographics)
  ethnicity: {
    filterId: FilterId.ETHNICITY,
    selectionListId: 'ethnicity',
  },
  gender: { filterId: FilterId.GENDER, selectionListId: 'gender' },
  highest_degree: {
    filterId: FilterId.EDUCATION,
    selectionListId: 'highest_degree',
  },
  seniority: {
    filterId: FilterId.SENIORITY,
    selectionListId: 'seniority',
  },
};

export const isTreeFilterMapping = (
  m: ScreenerFilterKeyMapping
): m is ScreenerTreeFilterMapping => 'levelId' in m && m.levelId != null;
